import debounce from 'lodash.debounce'
import { useCallback, useEffect, useRef, useState } from 'react'

export const useDebouncedInputChange = <T>(
  fnToDefer: (value?: string) => Promise<T>,
  fnToImmediatelyLaunch?: (value?: string) => void,
  delayMs = 200,
) => {
  // Debounce ref
  const { current: debouncedFn } = useRef(debounce(fnToDefer, delayMs))

  // Cleanup function
  const cleanup = useCallback(() => {
    debouncedFn.cancel()
  }, [])

  // Handle input change
  const handleInputChange = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      setLoading(true)
      const value = e.target.value
      cleanup() // cancel previous request
      if (fnToImmediatelyLaunch) {
        fnToImmediatelyLaunch(value)
      }
      const res = await debouncedFn(value)
      setLoading(false)
      return res
    },
    [cleanup, fnToImmediatelyLaunch],
  )

  const [loading, setLoading] = useState(false)

  // Cleanup on unmount
  useEffect(() => {
    return cleanup
  }, [cleanup])

  return {
    handleInputChange,
    cancel: cleanup,
    loading,
  }
}
